/**
 * /billing — self-serve subscription management + Paddle checkout (ADR-0026
 * PR-3b-3). Shows the current plan (from /subscriptions/me) and, for self-serve
 * tiers, opens a Paddle overlay to convert a trial, upgrade, or buy more seats.
 * An INVOICED tenant (ADR-0032 D7 — Enterprise, Founding, or anyone billed by
 * invoice; server-derived `billingChannel`, never the tier) sees its invoices and
 * how to pay instead (D8).
 *
 * Admin-layout only (the authed.tsx beforeLoad bounces role === 'employee').
 * The load-bearing invariant: nothing here asserts subscription state from the
 * checkout callback — /subscriptions/me is the sole source of truth, and the UI
 * shows "confirming…" until the webhook flips it (see usePaddleCheckout).
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedLayoutRoute } from './authed.js';
import {
  cancelSubscription,
  resumeSubscription,
  getSubscriptionMe,
  type BillingInterval,
  type SubscriptionMe,
  type SubscriptionTier,
} from '../api/subscriptions.js';
import { usePaddleCheckout } from '../lib/paddle.js';
import { downloadMyInvoicePdf, getMyInvoices, type TenantInvoice } from '../api/invoices.js';
import { ApiError } from '../api/client.js';
import { UpgradeContactModal } from '../components/UpgradeContactModal.js';
import { formatDate, formatMoney } from '../lib/format.js';
import type { CurrencyCode } from '@kora/types';

export const billingRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/billing',
  component: BillingPage,
});

interface PlanCard {
  tier: Exclude<SubscriptionTier, 'enterprise'>;
  name: string;
  monthly: number; // USD per employee / month
  annual: number; // USD per employee / year
}

// Display prices (STRATEGY §3.2). The ACTUAL charge comes from Paddle via the
// resolved priceId — these are the marketing figures for the cards only.
const PLANS: PlanCard[] = [
  { tier: 'starter', name: 'Starter', monthly: 6, annual: 57.6 },
  { tier: 'growth', name: 'Growth', monthly: 10, annual: 96 },
];

function BillingPage(): JSX.Element {
  const [interval, setInterval] = useState<BillingInterval>('month'); // default monthly (settled)
  const { data, isLoading, isError } = useQuery({ queryKey: ['subscription', 'me'], queryFn: getSubscriptionMe });
  const checkout = usePaddleCheckout();

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Billing &amp; plan</h1>
        <p className="text-sm text-gray-400">
          {data?.billingChannel === 'invoiced'
            ? 'Your plan, your invoices, and how to pay them.'
            : 'Manage your subscription, upgrade, or add seats.'}
        </p>
      </header>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : isError || !data ? (
        <p className="text-sm text-red-400" role="alert">
          Could not load your subscription.
        </p>
      ) : (
        <>
          <CurrentPlanPanel me={data} />

          {/*
            These branches read `status`; the SubscriptionBanner reads
            `effectiveStatus`. They cannot contradict for suspended/cancelled:
            computeEffectiveStatus only diverges from status for an EXPIRED TRIAL
            (→ read_only) — every other status passes through unchanged. So a
            `status === 'cancelled'|'suspended'` sub has the same effectiveStatus,
            and /billing + the banner agree on what is self-serviceable.
          */}
          {/* ADR-0032 D7: branch on the server's billingChannel, NOT the tier. A
              tier check sent invoiced Starter/Growth tenants (Founding, non-USD)
              to a Paddle checkout they cannot use. */}
          {data.billingChannel === 'invoiced' ? (
            <InvoicedBillingPanel />
          ) : data.status === 'suspended' ? (
            // An operator freeze (fraud/abuse) is NOT self-served out of — no
            // checkout, contact support (mirrors the terminal `suspended` state
            // in the billing state machine).
            <SuspendedNotice />
          ) : (
            // cancelled falls through here — UpgradePanel renders in "Resubscribe"
            // mode (cancelled → active is a legal paid reactivation).
            <UpgradePanel
              me={data}
              interval={interval}
              onInterval={setInterval}
              configured={checkout.configured}
              onChoose={(tier) => void checkout.openCheckout(tier, interval)}
              busy={checkout.phase === 'opening'}
            />
          )}
        </>
      )}

      {checkout.warning ? (
        <div role="alert" className="rounded border border-amber/40 bg-amber/10 px-3 py-2 text-sm text-amber">
          {checkout.warning}
        </div>
      ) : null}

      {checkout.phase === 'confirming' || checkout.phase === 'success' || checkout.phase === 'timeout' ? (
        <ConfirmingOverlay phase={checkout.phase} onClose={checkout.dismiss} />
      ) : null}
    </section>
  );
}

function CurrentPlanPanel({ me }: { me: SubscriptionMe }): JSX.Element {
  const seats = me.employeeCap === null ? `${me.employeeCount} employees` : `${me.employeeCount} of ${me.employeeCap} seats`;
  return (
    <div className="rounded border border-gray-800 p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium capitalize">{me.tier} plan</h2>
        {me.isFoundingCustomer ? (
          <span className="rounded-full bg-violet/15 px-2 py-0.5 text-xs text-violet">Founding customer</span>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
        <Row label="Status" value={<span className="capitalize">{me.effectiveStatus.replace('_', ' ')}</span>} />
        <Row label="Seats" value={seats} />
        {me.periodState === 'trial' && me.daysRemainingInTrial !== null ? (
          <Row
            label="Trial"
            value={me.daysRemainingInTrial === 0 ? 'Ends today' : `${me.daysRemainingInTrial} days left`}
          />
        ) : me.currentPeriodEnd && (me.periodState === 'renews' || me.periodState === 'period_ended') ? (
          // "Renews" is a claim about the FUTURE, and it can be wrong on two
          // different axes. The two conditions above are about STATUS (a cancelled
          // sub won't renew; a scheduled-cancel shows its own notice below). The
          // third is about the DATE: a lapsed subscription still carries a
          // currentPeriodEnd — one already behind us — so "Renews 2026-07-07"
          // states something that has visibly already not happened. Live on three
          // tenants (baqis, finca-drc, finca-rdc) when found on 2026-08-03.
          // Past ⇒ report what the date actually is: the period ended.
          me.periodState === 'period_ended' ? (
            <Row label="Period ended" value={formatDate(me.currentPeriodEnd)} />
          ) : (
            <Row label="Renews" value={formatDate(me.currentPeriodEnd)} />
          )
        ) : null}
        <Row label="Billing currency" value={me.billingCurrency} />
      </dl>
      <CancelSection me={me} />
    </div>
  );
}

/**
 * Cancel control (ADR-0026 follow-up). Three states:
 *   - already scheduled → a plain "cancels on <date>, access until then" notice;
 *   - eligible (active Paddle sub) → a "Cancel subscription" link → confirm modal;
 *   - otherwise (trial/manual, cancelled) → nothing (a trial just lapses).
 * The copy states cancel-vs-refund plainly: keep access to period end, no refund.
 */
function CancelSection({ me }: { me: SubscriptionMe }): JSX.Element | null {
  const [confirming, setConfirming] = useState(false);
  const qc = useQueryClient();
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['subscription', 'me'] });
  const mutation = useMutation({
    mutationFn: cancelSubscription,
    onSuccess: () => {
      setConfirming(false);
      invalidate();
    },
  });
  const resume = useMutation({ mutationFn: resumeSubscription, onSuccess: invalidate });

  if (me.cancelScheduledAt) {
    // The scheduled moment has arrived but the subscription.canceled webhook
    // hasn't landed yet (status is still active) — the same webhook-delivery gap
    // `resumeGuard` refuses server-side with CANCELLATION_IN_PROGRESS. Without
    // this branch the banner reads "Scheduled to cancel on <a past date>. You
    // keep full access until then" AND offers a Resume button the server will
    // reject — the UI offering an action it cannot complete.
    const finalising = me.periodState === 'cancel_finalising';
    return (
      <div className="rounded border border-amber/40 bg-amber/10 px-3 py-2 text-sm text-amber" role="status">
        {finalising ? (
          <p>
            Your cancellation date (<strong>{formatDate(me.cancelScheduledAt)}</strong>) has passed and the cancellation
            is being finalised now. It won&apos;t renew, and there is nothing further to do. This is not a refund of the
            current period.
          </p>
        ) : (
          <>
        <p>
          Scheduled to cancel on <strong>{formatDate(me.cancelScheduledAt)}</strong>. You keep full access until then; it
          won&apos;t renew after that. This is not a refund of the current period.
        </p>
        <button
          type="button"
          onClick={() => resume.mutate()}
          disabled={resume.isPending}
          className="mt-2 text-sm font-medium underline hover:no-underline disabled:opacity-50"
        >
          {resume.isPending ? 'Reversing…' : 'Keep my plan'}
        </button>
        {resume.isError ? (
          <span className="ml-3 text-red-400" role="alert">
            Couldn&apos;t reverse it. Please try again.
          </span>
        ) : null}
          </>
        )}
      </div>
    );
  }

  const eligible = me.paymentProvider === 'paddle' && me.status !== 'cancelled';
  if (!eligible) return null;

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="text-sm text-gray-400 underline hover:text-gray-200"
      >
        Cancel subscription
      </button>
      {confirming ? (
        <ConfirmCancelModal
          periodEnd={me.currentPeriodEnd}
          busy={mutation.isPending}
          error={mutation.isError ? 'Could not schedule your cancellation. Please try again.' : null}
          onKeep={() => setConfirming(false)}
          onConfirm={() => mutation.mutate()}
        />
      ) : null}
    </div>
  );
}

function ConfirmCancelModal({
  periodEnd,
  busy,
  error,
  onKeep,
  onConfirm,
}: {
  periodEnd: string | null;
  busy: boolean;
  error: string | null;
  onKeep: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const until = periodEnd ? formatDate(periodEnd) : 'the end of your current period';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
      <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-950 p-6 space-y-4">
        <h3 id="cancel-title" className="text-lg font-medium">Cancel your subscription?</h3>
        <ul className="space-y-2 text-sm text-gray-300 list-disc pl-5">
          <li>You&apos;ll keep <strong>full access until {until}</strong> — the period you&apos;ve already paid for.</li>
          <li>Your plan <strong>won&apos;t renew</strong> after that.</li>
          <li>This <strong>is not a refund</strong> of the current period.</li>
        </ul>
        {error ? <p className="text-sm text-red-400" role="alert">{error}</p> : null}
        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onKeep} disabled={busy} className="rounded px-4 py-2 text-sm text-gray-300 hover:text-white disabled:opacity-50">
            Keep my plan
          </button>
          <button type="button" onClick={onConfirm} disabled={busy} className="rounded bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50">
            {busy ? 'Scheduling…' : 'Cancel subscription'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }): JSX.Element {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-900 py-1">
      <dt className="text-gray-400">{label}</dt>
      <dd className="text-white text-right">{value}</dd>
    </div>
  );
}

function UpgradePanel({
  me,
  interval,
  onInterval,
  configured,
  onChoose,
  busy,
}: {
  me: SubscriptionMe;
  interval: BillingInterval;
  onInterval: (i: BillingInterval) => void;
  configured: boolean;
  onChoose: (tier: Exclude<SubscriptionTier, 'enterprise'>) => void;
  busy: boolean;
}): JSX.Element {
  // A cancelled tenant sees the same self-serve checkout re-framed as a paid
  // reactivation: cancelled → active is a legal transition (the webhook relinks
  // the new Paddle ref + re-grants the seat privilege). "Buy more seats" / "Choose"
  // both become "Resubscribe", and no card is flagged "Current plan" (there is no
  // current plan while cancelled).
  const resubscribe = me.status === 'cancelled';
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium">{resubscribe ? 'Reactivate your plan' : 'Choose a plan'}</h2>
        <IntervalToggle interval={interval} onChange={onInterval} />
      </div>

      {resubscribe ? (
        <p className="text-sm text-gray-400">
          Your subscription is cancelled. Choose a plan below to reactivate — you&apos;ll be billed for your current team size.
        </p>
      ) : null}

      {!configured ? (
        <p role="alert" className="rounded border border-amber/40 bg-amber/10 px-3 py-2 text-sm text-amber">
          Checkout is temporarily unavailable. Please try again later or contact support.
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2">
        {PLANS.map((plan) => {
          const price = interval === 'month' ? plan.monthly : plan.annual;
          const isCurrent = me.tier === plan.tier;
          return (
            <div key={plan.tier} className="rounded border border-gray-800 p-5 space-y-3">
              <div className="flex items-baseline justify-between">
                <h3 className="text-lg font-medium">{plan.name}</h3>
                {isCurrent && !resubscribe ? <span className="text-xs text-gray-500">Current plan</span> : null}
              </div>
              <p className="text-2xl font-serif">
                ${price.toFixed(2)}
                <span className="text-sm text-gray-400"> / employee / {interval === 'month' ? 'month' : 'year'}</span>
              </p>
              <button
                type="button"
                disabled={!configured || busy}
                onClick={() => onChoose(plan.tier)}
                className="w-full rounded bg-indigo-primary px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40"
              >
                {busy ? 'Opening…' : resubscribe ? 'Resubscribe' : isCurrent ? 'Buy more seats' : `Choose ${plan.name}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The `suspended` terminal state (operator freeze). Unlike `cancelled`, a suspend
 * is NOT self-served out of — no checkout, contact support. Mirrors the empty
 * outgoing-transition set for `suspended` in the billing state machine.
 */
function SuspendedNotice(): JSX.Element {
  return (
    <div className="rounded border border-gray-800 p-5 space-y-2">
      <h2 className="text-lg font-medium">Account suspended</h2>
      <p className="text-sm text-gray-400">
        Your account is suspended and self-serve billing is unavailable. Please contact us at{' '}
        <a href="mailto:hello@lumohr.com" className="underline hover:text-gray-200">
          hello@lumohr.com
        </a>{' '}
        to restore access.
      </p>
    </div>
  );
}

function IntervalToggle({ interval, onChange }: { interval: BillingInterval; onChange: (i: BillingInterval) => void }): JSX.Element {
  return (
    <div className="inline-flex rounded border border-gray-800 text-sm" role="group" aria-label="Billing interval">
      {(['month', 'year'] as const).map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          aria-pressed={interval === i}
          className={`px-3 py-1 ${interval === i ? 'bg-white text-black' : 'text-gray-400 hover:text-white'}`}
        >
          {i === 'month' ? 'Monthly' : 'Annual · save 20%'}
        </button>
      ))}
    </div>
  );
}

/**
 * ADR-0032 D8 — the invoiced tenant's billing surface. No new endpoint: the
 * invoices come from GET /v1/invoices/me (exempt from the read-only write-lock,
 * so a restricted tenant can still see what it owes). Payment is by bank
 * transfer, confirmed by our team — so this page shows WHAT to pay and HOW,
 * and never offers a checkout.
 */
function InvoicedBillingPanel(): JSX.Element {
  const [contactOpen, setContactOpen] = useState(false);
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['invoices', 'me'], queryFn: getMyInvoices });
  // Invoices are owner/admin/finance only (server-enforced, routes/invoices.ts).
  // HR and IT can reach /billing, so a 403 here is expected, not a failure.
  const forbidden = error instanceof ApiError && error.status === 403;
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Unpaid first (the oldest due date is the one to act on), then the rest newest-first.
  const invoices = data?.invoices ?? [];
  const unpaid = invoices
    .filter((i) => i.status === 'issued' || i.status === 'overdue')
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  const history = invoices.filter((i) => i.status === 'paid' || i.status === 'voided');

  const download = (inv: TenantInvoice): void => {
    setDownloadError(null);
    downloadMyInvoicePdf(inv).catch(() => setDownloadError(`Could not download ${inv.number}. Please try again.`));
  };

  return (
    <div className="rounded border border-gray-800 p-5 space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-medium">Invoices &amp; payment</h2>
        <p className="text-sm text-gray-400">
          Your plan is billed by invoice. To renew, change seats, or change your contract terms, contact our billing team.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading invoices…</p>
      ) : forbidden ? (
        <p className="text-sm text-gray-400">Invoices are visible to your organization&apos;s owners, admins and finance team.</p>
      ) : isError ? (
        <p className="text-sm text-red-400" role="alert">
          Could not load your invoices.
        </p>
      ) : (
        <>
          <InvoiceTable title="Awaiting payment" invoices={unpaid} onDownload={download} empty="No invoices awaiting payment." />
          {history.length > 0 ? <InvoiceTable title="History" invoices={history} onDownload={download} /> : null}
        </>
      )}

      {downloadError ? (
        <p className="text-sm text-red-400" role="alert">
          {downloadError}
        </p>
      ) : null}

      <div className="rounded border border-gray-800 bg-gray-900/40 px-4 py-3 text-sm text-gray-300 space-y-1">
        <p className="font-medium text-white">How to pay</p>
        <p>
          Pay by bank transfer to Mulanji Technologies Solution Limited, quoting your invoice number as the reference. Bank
          details are in the email your invoice came with. We confirm payment on our side, usually within one business day.
        </p>
      </div>

      <button
        type="button"
        onClick={() => setContactOpen(true)}
        className="rounded border border-indigo-primary px-3 py-2 text-sm text-indigo-primary hover:bg-indigo-primary hover:text-white"
      >
        Contact billing
      </button>
      <UpgradeContactModal open={contactOpen} onClose={() => setContactOpen(false)} intent="billing" />
    </div>
  );
}

const INVOICE_STATUS_LABEL: Record<TenantInvoice['status'], string> = {
  issued: 'Due',
  overdue: 'Overdue',
  paid: 'Paid',
  voided: 'Voided',
};

function InvoiceTable({
  title,
  invoices,
  onDownload,
  empty,
}: {
  title: string;
  invoices: TenantInvoice[];
  onDownload: (inv: TenantInvoice) => void;
  empty?: string;
}): JSX.Element {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-300">{title}</h3>
      {invoices.length === 0 ? (
        <p className="text-sm text-gray-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-1 pr-4 font-normal">Invoice</th>
                <th className="py-1 pr-4 font-normal">Period</th>
                <th className="py-1 pr-4 font-normal">Due</th>
                <th className="py-1 pr-4 font-normal text-right">Amount</th>
                <th className="py-1 pr-4 font-normal">Status</th>
                <th className="py-1 font-normal">
                  <span className="sr-only">PDF</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-gray-800">
                  <td className="py-2 pr-4 font-mono">{inv.number}</td>
                  <td className="py-2 pr-4 text-gray-400">
                    {inv.servicePeriodStart && inv.servicePeriodEnd
                      ? `${formatDate(inv.servicePeriodStart)} to ${formatDate(inv.servicePeriodEnd)}`
                      : '—'}
                  </td>
                  <td className="py-2 pr-4">{formatDate(inv.dueDate)}</td>
                  <td className="py-2 pr-4 text-right">{formatMoney(inv.totalAmount, inv.currency as CurrencyCode)}</td>
                  <td className={`py-2 pr-4 ${inv.status === 'overdue' ? 'text-red-400' : 'text-gray-300'}`}>
                    {INVOICE_STATUS_LABEL[inv.status]}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => onDownload(inv)}
                      className="text-indigo-primary underline hover:no-underline"
                      aria-label={`Download ${inv.number} PDF`}
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConfirmingOverlay({ phase, onClose }: { phase: 'confirming' | 'success' | 'timeout'; onClose: () => void }): JSX.Element {
  const body =
    phase === 'confirming'
      ? { title: 'Confirming your upgrade…', text: 'Payment received. Finalising your account — this only takes a moment.', showClose: false }
      : phase === 'success'
        ? { title: "You're all set 🎉", text: 'Your plan is active.', showClose: true }
        : { title: 'Payment succeeded', text: 'Your account will update shortly. You can safely close this.', showClose: true };
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="w-full max-w-sm rounded-lg bg-gray-950 border border-gray-800 p-6 text-center space-y-3">
        <h3 className="text-lg font-medium">{body.title}</h3>
        <p className="text-sm text-gray-400">{body.text}</p>
        {phase === 'confirming' ? <div className="text-sm text-gray-500">Confirming…</div> : null}
        {body.showClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-indigo-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
          >
            Done
          </button>
        ) : null}
      </div>
    </div>
  );
}
